import React, { useState } from "react";

import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from "@mui/material";

import { NewsComments } from "../News-comments";
import { Actions } from "./Actions";
import { NewsDeals } from "./Deals";
import { NewsHeaderComponent } from "./Header";

export interface NewsHeader {
  avatarUrl: string;
  title: string;
  source?: string;
  date?: string;
  category?: string;
  timeAgo: string;
}

export interface NewsTag {
  label: string;
  color?: string;
}

export interface NewsDeal {
  name: string;
  commitment: string;
  roi: string;
  avatarUrl: string;
}

export interface NewsActions {
  showBookmark?: boolean;
  showSend?: boolean;
  showComment?: boolean;
  showSeeItem?: boolean;
  commentsCount?: number;
}

export interface NewsItem {
  id: string;
  type: "article" | "deals";
  header: NewsHeader;
  tag?: NewsTag;
  title: string;
  description?: string;
  content?: string;
  imageUrl?: string;
  deals?: NewsDeal[];
  seeFullListLink?: string;
  actions: NewsActions;
  currentUserAvatarUrl?: string;
}

interface Props {
  newsItem: NewsItem;
}

export const News: React.FC<Props> = ({ newsItem }) => {
  const { header, tag, title, description, content, imageUrl, deals, actions, currentUserAvatarUrl } = newsItem;
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);

  const handleCommentClick = () => {
    setIsCommentsOpen(!isCommentsOpen);
  };

  const handleAddComment = (text: string) => {
    // TODO: Implement comment creation
    console.log("Add comment:", text);
  };

  const handleReply = (commentId: string, text: string) => {
    // TODO: Implement reply creation
    console.log("Reply to comment:", commentId, "Text:", text);
  };

  return (
    <Stack
      sx={{
        bgcolor: "white",
        borderRadius: "8px",
        p: "16px",
        gap: "10px",
        boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)"
      }}
    >
      <NewsHeaderComponent header={header} tag={tag} />

      {/* Title */}
      <Typography
        sx={{
          fontSize: "24px",
          fontWeight: 700,
          color: "#071f35",
          lineHeight: "32px"
        }}
      >
        {title}
      </Typography>

      {/* Description */}
      {description && (
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            color: "#071f35",
            lineHeight: "24px"
          }}
        >
          {description}
        </Typography>
      )}

      {/* Content */}
      {content && (
        <Box
          sx={{
            // borderBottom: deals || imageUrl ? "1px solid #dbe8f5" : "none",
            // pb: deals || imageUrl ? "10px" : 0
            borderBottom: "1px solid #dbe8f5",
            pb: "10px"
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 400,
              color: "#071f35",
              lineHeight: "24px"
            }}
          >
            {content}
          </Typography>
        </Box>
      )}

      {/* Deal Items */}
      {deals && <NewsDeals deals={deals} />}

      {/* Image */}
      {imageUrl && (
        <Box
          component="img"
          src={imageUrl}
          sx={{
            width: "100%",
            height: "333px",
            objectFit: "cover",
            borderRadius: "8px"
          }}
        />
      )}

      {/* Actions */}
      <Actions actions={actions} onCommentClick={handleCommentClick} />

      {/* Comments Accordion */}
      {actions.showComment && (
        <Accordion
          expanded={isCommentsOpen}
          sx={{
            boxShadow: "none",
            "&:before": {
              display: "none"
            },
            "&.Mui-expanded": {
              margin: 0
            }
          }}
        >
          <AccordionSummary
            sx={{
              display: "none",
              minHeight: 0,
              "&.Mui-expanded": {
                minHeight: 0
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
                "&.Mui-expanded": {
                  margin: 0
                }
              }
            }}
          />
          <AccordionDetails
            sx={{
              p: 0,
              height: "400px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <NewsComments
              isOpen={isCommentsOpen}
              currentUserAvatarUrl={currentUserAvatarUrl}
              onAddComment={handleAddComment}
              onReply={handleReply}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
};
